import React, { useRef, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useRouteMatch } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { PropTypes } from 'prop-types';
import { injectIntl } from 'react-intl';

import FormGroupTypes from 'common/forms/formGroupTypes';

import Button from 'components/Common/Buttons';
import CustomerAvatar from 'components/CustomerAvatar';
import LocalisedMessage from 'components/Common/LocalisedMessage';

import {
  getFormPdf,
  clearDefaultForm,
  getFormGroupPdf,
  clearPdfDownload,
  getCollectableSupportingDocPdf,
  getFormLinkedCollectableSupportingDocId,
  getFormGroupLinkedCollectableSupportingDocId
} from 'actions';

// Hook
function usePrevious(value) {
  // The ref object is a generic container whose current property is mutable ...
  // ... and can hold any value, similar to an instance property on a class
  const ref = useRef();
  // Store current value in ref
  useEffect(() => {
    ref.current = value;
  }, [value]); // Only re-run if value changes
  // Return previous value (happens before update in useEffect above)
  return ref.current;
}

const SuccessPage = ({ intl }) => {
  const dispatch = useDispatch();
  const match = useRouteMatch();

  const formTypes = useSelector(state => state.app.onlineConfig?.formTypes);
  const previewOnlyMode = useSelector(state => state.app.onlineConfig?.previewOnlyMode || null);
  const defaultFormType = useSelector(state => state.app.onlineConfig?.defaultFormType) || null;
  const pdfData = useSelector(state => state.form.pdfData);
  const isFetchingPdfForm = useSelector(state => state.form.isFetchingPdfForm);
  const downloadRequestFormType = useSelector(state => state.form.downloadRequestFormType);
  const linkedCollectableSupportingDocId = useSelector(state => state.form.linkedCollectableSupportingDocId);
  const isFetchingCollectablePdf = useSelector(state => state.supportingDocuments.isFetchingCollectablePdf);
  const collectableSupportingDocPdfData = useSelector(state => state.supportingDocuments.collectableSupportingDocPdfData);

  const prevIsFetchingPdfForm = usePrevious(isFetchingPdfForm);
  const prevIsFetchingCollectablePdfForm = usePrevious(isFetchingCollectablePdf);
  const prevDownloadRequestFormType = usePrevious(downloadRequestFormType);
  const [pdfDownload, setPdfDownload] = useState(null);

  useEffect(() => {
    if (defaultFormType) {
      dispatch(clearDefaultForm());
    }

    return () => {
      if (pdfDownload) {
        URL.revokeObjectURL(pdfDownload);
      }
      dispatch(clearPdfDownload());
    };
  }, []);

  useEffect(() => {
    const { params: { formType } } = match;

    if (prevIsFetchingPdfForm && !isFetchingPdfForm && pdfData) {
      setPdfDownload(pdfData);
      const link = document.createElement('a');
      link.href = pdfData;
      link.setAttribute('download', `${ formType ?? prevDownloadRequestFormType }.pdf`);
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
    }
  }, [isFetchingPdfForm, pdfData]);

  useEffect(() => {
    if (prevIsFetchingCollectablePdfForm && !isFetchingCollectablePdf && collectableSupportingDocPdfData) {
      setPdfDownload(collectableSupportingDocPdfData);
      const link = document.createElement('a');
      link.href = collectableSupportingDocPdfData;
      link.setAttribute('download', 'affidavit.pdf');
      link.setAttribute('target', '_blank');
      document.body.appendChild(link);
      link.click();
    }
  }, [isFetchingCollectablePdf, collectableSupportingDocPdfData]);

  useEffect(() => {
    if (linkedCollectableSupportingDocId === null) {
      const { params: { formId, groupId } } = match;
      if (groupId) {
        dispatch(getFormGroupLinkedCollectableSupportingDocId(groupId, intl));
      } else {
        dispatch(getFormLinkedCollectableSupportingDocId(formId, intl));
      }
    }
  }, []);

  const getFormTypeMarkup = () => {
    const { params: { formType, formGroupType } } = match;
    let form = 'form';
    let version = 'revision';

    if (formTypes) {
      const formTypeDetails = formTypes.filter(ft => ft.formType === formType);

      if (formTypeDetails && formTypeDetails[0]) {
        const { title, revision } = formTypeDetails[0];
        form = title;
        version = revision;
      }
    }

    if (formGroupType) {
      const formGroup = FormGroupTypes.find(group => group.key === formGroupType);
      const markup = formGroup.formTypes.map((f) => {
        const formTypeDetails = formTypes.filter(ft => ft.formType === f);
        if (formTypeDetails && formTypeDetails[0]) {
          const { title, revision } = formTypeDetails[0];
          form = title;
          version = revision;
        }
        return (
          <div className="mv-2 form-type-bold form-type-container" key={ f }>
            <p className="form-type">{ form }</p>
            {version && <p className="form-type-version">Rev. { version }</p> }
          </div>
        );
      });

      return markup;
    }

    return (
      <div className="mv-2 form-type-bold form-type-container">
        <p className="form-type">{ form }</p>
        { version && <p className="form-type-version">Rev. { version }</p> }
      </div>
    );
  };

  const downloadPdf = (groupFormType) => {
    const { params: { formType, formId, groupId } } = match;

    if (formId) {
      dispatch(getFormPdf(formId, formType, intl));
    }

    if (groupFormType && groupId) {
      dispatch(getFormGroupPdf(groupId, groupFormType, intl));
    }
  };

  const downloadCollectableSupportingDoc = () => {
    if (linkedCollectableSupportingDocId !== null) {
      dispatch(getCollectableSupportingDocPdf(
        {
          supportingDocId: linkedCollectableSupportingDocId,
          collectableSupportingDocType: 'AffidavitOfUnchangedStatus',
          signature: null
        },
        intl,
        false
      ));
    }
  };

  const { params: { formType, formGroupType } } = match;
  const formGroup = FormGroupTypes.find(group => group.key === formGroupType);

  const buttonText = intl.formatMessage({ id: isFetchingPdfForm ? 'formPreview.downloading.buttonText' : 'buttons.download.buttonText' });
  const affidavitText = intl.formatMessage({ id: isFetchingCollectablePdf ? 'formPreview.downloading.buttonText' : 'formPreview.download.affidavit.buttonText' });

  return (
    <section className="success-page">
      <div className="row">
        <div className="col-12">
          <CustomerAvatar className="mobile-username" />
        </div>
      </div>
      <div className="row">
        <div className="col-12 offset-sm-2 col-sm-8 offset-md-3 col-md-6 offset-lg-4 col-lg-4 content">
          <h1><LocalisedMessage id="successPage.successHeader" /></h1>
          <span className="check-icon">
            <FontAwesomeIcon icon="check-circle" size="4x" />
          </span>
          <p><LocalisedMessage id={ previewOnlyMode ? 'successPage.previewOnlyMode.intro' : 'successPage.intro' } /></p>
          { getFormTypeMarkup() }
          <p><LocalisedMessage id="successPage.disclaimer" /></p>
          <div className="row mt-4">
            <div className="col-12 mb-4">
              { formType && <Button
                className={ `btn-download-preview-pdf mr-0 ${ isFetchingPdfForm ? 'btn-active-download' : '' }` }
                value={ `${ buttonText } ${ formTypes.find(ft => ft.formType === formType.toLowerCase())?.title }` }
                onClick={ () => downloadPdf() }
              /> }
              { formGroup && formGroup.formTypes.map(f => <Button
                key={ f }
                value={ `${ buttonText } ${ formTypes.filter(ft => ft.formType === f) ? formTypes.filter(ft => ft.formType === f)[0].title : f.toUpperCase() }` }
                className={ `btn-secondary btn-download mr-0 ${ isFetchingPdfForm && downloadRequestFormType === f ? 'btn-active' : '' }` }
                disabled={ isFetchingPdfForm }
                onClick={ () => downloadPdf(f) }
              />) }
              { linkedCollectableSupportingDocId && <Button
                className={ `${ formGroup ? 'btn-secondary btn-download' : 'btn-download-preview-pdf' } mr-0 ${ isFetchingCollectablePdf && !formGroup ? 'btn-active-download' : '' } ${ isFetchingCollectablePdf && formGroup ? 'btn-active' : '' }` }
                value={ affidavitText }
                onClick={ () => downloadCollectableSupportingDoc() }
              /> }
            </div>
            <div className="col-12 link">
              <Link to="/form-selection" className="btn btn-link"><LocalisedMessage id="successPage.formSelectionLink" /></Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

SuccessPage.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired
  }).isRequired
};

export default injectIntl(SuccessPage);
